// Platform-wide settings from GET api/settings — a key/value store the admin
// edits. Read once per session and cached; nothing here is hardwired, so an
// environment without the endpoint simply has no settings (and no age
// warnings), never a built-in default.
import { SERVER_URL } from "../../constants";

let cache = null; // { available, settings: { [key]: value } } once loaded
let inflight = null;

// Resolves to { available, settings }. `available` is false when the endpoint
// isn't there (or failed) — distinct from "loaded, but this key isn't set", so
// a screen can say "checks unavailable" rather than looking merely clean.
export async function loadAppSettings(token, { force = false } = {}) {
  if (cache && !force) return cache;
  if (inflight && !force) return inflight;
  inflight = (async () => {
    try {
      const res = await fetch(`${SERVER_URL}api/settings`, {
        headers: { Authorization: `Bearer ${token || sessionStorage.getItem("bearer")}` },
      });
      if (!res.ok) throw new Error(`settings ${res.status}`);
      const rows = await res.json();
      const settings = {};
      (Array.isArray(rows) ? rows : []).forEach((r) => {
        if (r && r.key != null) settings[r.key] = r.value;
      });
      cache = { available: true, settings };
      return cache;
    } catch {
      cache = { available: false, settings: {} };
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

export const clearAppSettingsCache = () => {
  cache = null;
};

// Why there's no age range to check against — for the UI to say so instead of
// silently showing nothing. null when a range IS configured.
export function ageCheckUnavailableReason(state) {
  if (!state) return "Age checks unavailable — settings not loaded.";
  if (!state.available) return "Age checks unavailable — this backend has no settings endpoint (GET api/settings).";
  if (!studentAgeRange(state.settings)) {
    return "Age checks unavailable — no accepted age range is set (Admin → Settings: student.age.min / student.age.max).";
  }
  return null;
}

// The accepted student age range, or null when either bound isn't configured.
export function studentAgeRange(settings) {
  const min = Number(settings?.["student.age.min"]);
  const max = Number(settings?.["student.age.max"]);
  if (!Number.isInteger(min) || !Number.isInteger(max)) return null;
  return { min, max };
}

// Whole years old on `onDate` (YYYY-MM-DD; defaults to today). null when the
// DOB is missing or unreadable.
export function ageOn(dob, onDate) {
  const d = String(dob || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) return null;
  const o = onDate ? String(onDate).slice(0, 10) : new Date().toISOString().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(o)) return null;
  const [by, bm, bd] = d.split("-").map(Number);
  const [oy, om, od] = o.split("-").map(Number);
  let age = oy - by;
  if (om < bm || (om === bm && od < bd)) age -= 1;
  return age;
}

// "Age 57 on arrival — outside 8–17", or null when in range / not checkable.
export function ageWarning(dob, onDate, range, { when = "on arrival" } = {}) {
  if (!range) return null;
  const age = ageOn(dob, onDate);
  if (age == null) return null;
  if (age >= range.min && age <= range.max) return null;
  return `Age ${age} ${when} — outside ${range.min}–${range.max}`;
}
